'use client';

import { useMemo } from 'react';
import Image from 'next/image';
import { Modal, ChevronDown, useDisclosure, useI18n, useQueryState } from '@ywwwtseng/react-kit';
import { Typography } from '@ywwwtseng/tma-react-kit';
import { useWalletKitConnect } from '@ywwwtseng/wallet-kit';
import { useTonWalletKit } from '@ywwwtseng/ton-wallet-kit';
import { NETWORKS, NETWORK_NAMES } from '@/constants';

export function SelectNetwork({
  needConnected = false,
  network,
  onNetworkSelected,
}: {
  needConnected?: boolean;
  network: string | null;
  onNetworkSelected: (network: string) => void;
}) {
  const networks = useQueryState<string[]>('networks');
  const { t } = useI18n();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const walletKitConnect = useWalletKitConnect();
  const tonWalletKit = useTonWalletKit();
  const selected = useMemo(() => {
    return NETWORK_NAMES[network as keyof typeof NETWORK_NAMES] ?? network;
  }, [network]);

  if (!networks) {
    return null;
  }

  return (
    <>
      <div
        className="w-full flex items-center justify-between gap-1 pl-2 pr-3 bg-form-element rounded-lg text-sm h-10"
        onClick={onOpen}
      >
        <Typography size="2" weight={500}>
          {t('common.network')}
        </Typography>
        <div className="flex items-center gap-1">
          <Typography
            size="2"
            className={network ? 'text-foreground' : 'text-placeholder'}
          >
            {selected ?? 'Select Network'}
          </Typography>
          <ChevronDown width={20} height={20} />
        </div>
      </div>
      <Modal
        title="select network modal"
        classes={{ content: 'border-1 border-white/10' }}
        open={isOpen}
        onClose={onClose}
      >
        <Typography size="1">{t('select_network_modal.title')}</Typography>
        <div className="flex flex-col justify-center items-center h-full w-full gap-4 pb-4 px-4">
          {networks.includes(NETWORKS.TON) && (
            <div
              className="flex items-center justify-between w-full"
              onClick={() => {
                if (needConnected && !tonWalletKit.address) {
                  void tonWalletKit.connect();
                  onClose();
                  return;
                }

                onNetworkSelected(NETWORKS.TON);
                onClose();
              }}
            >
              <div className="flex items-center gap-2">
                <Image
                  src="/images/chains/ton.png"
                  alt="ton"
                  width={32}
                  height={32}
                />
              </div>
              <Typography size="1" weight={500}>
                {NETWORK_NAMES[NETWORKS.TON]}
              </Typography>
            </div>
          )}
          {/* 
        <div
          className="flex items-center justify-between w-full"
          onClick={() => {
            onChainSelected('trx');
            onClose();
          }}
        >
          <div className="flex items-center gap-2">
            <Image
              src="/images/chains/trx.png"
              alt="trx"
              width={32}
              height={32}
            />
          </div>
          <Typography size="2" weight={500}>
            Tron Network
          </Typography>
        </div> */}
          {networks.includes(NETWORKS.SOLANA) && (
            <div
              className="flex items-center justify-between w-full"
              onClick={() => {
                if (needConnected && walletKitConnect.accounts.solana.status !== 'connected') {
                  void walletKitConnect.open();
                  onClose();
                  return;
                }

                onNetworkSelected('solana');
                onClose();
              }}
            >
              <div className="flex items-center gap-2">
                <Image
                  src="/images/chains/solana.png"
                  className="rounded-full"
                  alt="evm"
                  width={32}
                  height={32}
                />
              </div>
              <Typography size="1" weight={500}>
                {NETWORK_NAMES[NETWORKS.SOLANA]}
              </Typography>
            </div>
          )}
          {networks.includes(NETWORKS.ETHEREUM) && (
            <div
              className="flex items-center justify-between w-full"
              onClick={() => {
                if (needConnected && walletKitConnect.accounts.ethereum.status !== 'connected') {
                  void walletKitConnect.open();
                  onClose();
                  return;
                }

                onNetworkSelected('ethereum');
                onClose();
              }}
            >
              <Image
                src="/images/chains/ethereum.png"
                alt="evm"
                width={32}
                height={32}
              />
              <Typography size="1" weight={500}>
                {NETWORK_NAMES[NETWORKS.ETHEREUM]}
              </Typography>
            </div>
          )}
          {networks.includes(NETWORKS.BSC) && (
            <div
              className="flex items-center justify-between w-full"
              onClick={() => {
                if (needConnected && walletKitConnect.accounts.bsc.status !== 'connected') {
                  void walletKitConnect.open();
                  onClose();
                  return;
                }

                onNetworkSelected('bsc');
                onClose();
              }}
            >
              <Image
                src="/images/chains/bsc.png"
                alt="bsc"
                width={32}
                height={32}
              />
              <Typography size="1" weight={500}>
                {NETWORK_NAMES[NETWORKS.BSC]}
              </Typography>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
